import React, { useEffect, useRef } from "react";
import { Box, Container, Typography, Button, Stack, Chip } from "@mui/material";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import NatureIcon from "@mui/icons-material/Nature";
import PhoneIcon from "@mui/icons-material/Phone";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import { useBooking } from "../context/BookingContext";

// ── Brand Palette ──────────────────────────────────────────────
const NAVY = "#050F2C";
const BLUE = "#1B4ED8";
const GREEN = "#72BF20";
const DARK = "#030A1A";

// ── Global CSS ─────────────────────────────────────────────────
const GLOBAL_CSS = `
  @keyframes rotateSlow {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
  @keyframes rotateSlowRev {
    from { transform: rotate(360deg); }
    to   { transform: rotate(0deg); }
  }
  @keyframes pulseGlow {
    0%,100% { opacity: 0.5; }
    50%      { opacity: 1; }
  }
  @keyframes shimmerText {
    0%   { background-position: -300% center; }
    100% { background-position:  300% center; }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes neonPulse {
    0%,100% { box-shadow: 0 0 8px #72BF2055, 0 0 20px #72BF2020; }
    50%      { box-shadow: 0 0 22px #72BF2099, 0 0 50px #72BF2044; }
  }
  @keyframes blink {
    0%,100% { opacity: 1; }
    50%      { opacity: 0.25; }
  }
  @keyframes orb1 {
    0%,100% { opacity: 0.5; }
    50%      { opacity: 0.85; }
  }
  @keyframes orb2 {
    0%,100% { opacity: 0.4; }
    50%      { opacity: 0.75; }
  }
`;

// ── Feature chips ──────────────────────────────────────────────
const FEATURES = [
  { icon: <NatureIcon sx={{ fontSize: 13 }} />, label: "Zero Emissions" },
  { icon: <SecurityIcon sx={{ fontSize: 13 }} />, label: "GPS Tracked Fleet" },
  { icon: <SpeedIcon sx={{ fontSize: 13 }} />, label: "Unlock in 2 Min" },
  { icon: <TrendingUpIcon sx={{ fontSize: 13 }} />, label: "₹1,999 / Week" },
];

// ── Stats ──────────────────────────────────────────────────────
const STATS = [
  { value: "5,000+", label: "Happy Riders", color: GREEN },
  { value: "100%", label: "Electric Fleet", color: "#60A5FA" },
  { value: "4.8 ★", label: "App Rating", color: "#FBBF24" },
  { value: "₹1,999", label: "Weekly Rental", color: "#A78BFA" },
];

export default function Hero() {
  const { openBookingModal } = useBooking();
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    const particles = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 55; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 1.6 + 0.3,
        dx: (Math.random() - 0.5) * 0.25,
        dy: (Math.random() - 0.5) * 0.25,
        alpha: Math.random() * 0.3 + 0.05,
        color: Math.random() > 0.55 ? "114,191,32" : "59,111,232",
      });
    }

    const COLS = 26, ROWS = 14;
    let tick = 0;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const gW = canvas.width / COLS;
      const gH = canvas.height / ROWS;
      for (let r = 0; r <= ROWS; r++) {
        for (let c = 0; c <= COLS; c++) {
          const wave = Math.sin(tick * 0.02 + c * 0.38 + r * 0.48) * 0.5 + 0.5;
          ctx.beginPath();
          ctx.arc(c * gW, r * gH, 1.1, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(114,191,32,${wave * 0.09 + 0.02})`;
          ctx.fill();
        }
      }
      particles.forEach((p) => {
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.alpha})`;
        ctx.fill();
      });
      tick++;
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  return (
    <>
      <style>{GLOBAL_CSS}</style>

      <Box component="section" id="hero" sx={{
        width: "100%", height: { xs: "130vh", md: "100vh" },
        background: `
          radial-gradient(ellipse 90% 65% at 8% 55%,  #0C1E60 0%, transparent 60%),
          radial-gradient(ellipse 55% 75% at 90% 10%, #0B2E14 0%, transparent 55%),
          radial-gradient(ellipse 45% 50% at 55% 95%, #0D1A40 0%, transparent 60%),
          ${DARK}
        `,
        position: "relative", overflow: "hidden",
        display: "flex", alignItems: "center",
      }}>
        {/* Animated Canvas */}
        <Box component="canvas" ref={canvasRef}
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} />

        {/* Top neon bar */}
        <Box sx={{
          position: "absolute", top: 0, left: 0, right: 0, height: "2px", zIndex: 10,
          background: `linear-gradient(90deg, transparent, ${BLUE} 20%, ${GREEN} 50%, ${BLUE} 80%, transparent)`,
          backgroundSize: "300% auto", animation: "shimmerText 5s linear infinite",
        }} />

        {/* Background glow orbs */}
        <Box sx={{ position: "absolute", top: "-12%", left: "-10%", width: { xs: 280, md: 560 }, height: { xs: 280, md: 560 }, borderRadius: "50%", background: `radial-gradient(circle, ${BLUE}35 0%, transparent 70%)`, filter: "blur(18px)", pointerEvents: "none", zIndex: 0, animation: "orb1 8s ease-in-out infinite" }} />
        <Box sx={{ position: "absolute", bottom: "-18%", right: "10%", width: { xs: 220, md: 460 }, height: { xs: 220, md: 460 }, borderRadius: "50%", background: `radial-gradient(circle, ${GREEN}22 0%, transparent 70%)`, filter: "blur(14px)", pointerEvents: "none", zIndex: 0, animation: "orb2 10s ease-in-out infinite 2s" }} />

        {/* Ambient rotating rings */}
        <Box sx={{ position: "absolute", top: "50%", left: { xs: "-22%", md: "-7%" }, transform: "translateY(-50%)", width: { xs: 320, md: 580 }, height: { xs: 320, md: 580 }, borderRadius: "50%", border: `1px solid ${GREEN}14`, borderTop: `2px solid ${GREEN}50`, animation: "rotateSlow 24s linear infinite", pointerEvents: "none", zIndex: 0, display: { xs: "none", md: "block" } }} />
        <Box sx={{ position: "absolute", top: "50%", left: { xs: "-16%", md: "-3%" }, transform: "translateY(-50%)", width: { xs: 220, md: 400 }, height: { xs: 220, md: 400 }, borderRadius: "50%", border: `1px dashed ${BLUE}22`, animation: "rotateSlowRev 16s linear infinite", pointerEvents: "none", zIndex: 0, display: { xs: "none", md: "block" } }} />

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 2, height: "100%", display: "flex", alignItems: "center" }}>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "48% 52%" },
            alignItems: "center",
            width: "100%",
            gap: { xs: 4, md: 3 },
          }}>

            {/* ══════════ LEFT ══════════ */}
            <Box sx={{ animation: "fadeUp 0.7s ease both" }}>
              {/* Live badge */}
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.2, bgcolor: "rgba(114,191,32,0.09)", border: `1px solid ${GREEN}38`, borderRadius: "100px", px: 2, py: 0.6, mb: { xs: 2, md: 2.5 }, backdropFilter: "blur(8px)" }}>
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: GREEN, boxShadow: `0 0 8px ${GREEN}`, animation: "blink 2s ease-in-out infinite" }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: GREEN, letterSpacing: "0.09em", textTransform: "uppercase" }}>
                  Live · Bangalore's #1 EV Rental Platform
                </Typography>
                <Box sx={{ bgcolor: `${GREEN}20`, border: `1px solid ${GREEN}50`, borderRadius: "4px", px: 0.8, py: 0.1 }}>
                  <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: GREEN }}>NEW</Typography>
                </Box>
              </Box>

              {/* Headline */}
              <Box sx={{ mb: { xs: 1.5, md: 2 }, animation: "fadeUp 0.8s ease 0.1s both" }}>
                <Typography component="h1" sx={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: { xs: "2.2rem", sm: "3rem", md: "3.2rem", lg: "3.8rem" }, lineHeight: 1.05, letterSpacing: "-0.04em", color: "#FFFFFF" }}>
                  Smarter Rides.
                </Typography>
                <Typography component="div" sx={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800, fontSize: { xs: "2.2rem", sm: "3rem", md: "3.2rem", lg: "3.8rem" }, lineHeight: 1.05, letterSpacing: "-0.04em", background: `linear-gradient(90deg, ${GREEN} 0%, #C8F060 30%, #A8E040 55%, ${GREEN} 80%)`, backgroundSize: "300% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", animation: "shimmerText 3s linear infinite" }}>
                  Greener Future.
                </Typography>
                <Typography component="div" sx={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: { xs: "1.1rem", sm: "1.4rem", md: "1.5rem", lg: "1.7rem" }, lineHeight: 1.3, color: "rgba(160,190,255,0.65)", letterSpacing: "-0.01em", mt: 0.5 }}>
                  Power India's EV Revolution.
                </Typography>
              </Box>

              {/* Description */}
              <Typography sx={{ fontSize: { xs: 13.5, md: 14.5 }, color: "#fff", fontWeight: 400, lineHeight: 1.7, mb: { xs: 2, md: 2.5 }, maxWidth: 490, animation: "fadeUp 0.8s ease 0.2s both" }}>
                Rent electric scooters effortlessly across Bangalore — instant unlock, affordable unlimited weekly plans at{" "}
                <Box component="span" sx={{ color: GREEN, fontWeight: 700 }}>₹1,999 / week</Box>{" "}
                with zero emissions and zero hassle.
              </Typography>

              {/* Feature chips */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.9, mb: { xs: 2.5, md: 3 }, animation: "fadeUp 0.8s ease 0.3s both" }}>
                {FEATURES.map((f) => (
                  <Chip key={f.label} icon={f.icon} label={f.label} size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.06)", color: "rgba(210,228,255,0.9)", border: "1px solid rgba(255,255,255,0.12)", backdropFilter: "blur(10px)", fontWeight: 600, fontSize: 11.5, height: 28, "& .MuiChip-icon": { color: GREEN, ml: 0.5 }, "&:hover": { bgcolor: `${GREEN}18`, borderColor: `${GREEN}50`, color: "#fff" }, transition: "all 0.2s ease" }}
                  />
                ))}
              </Box>

              {/* CTAs */}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={{ xs: 3, md: 3.5 }} sx={{ animation: "fadeUp 0.8s ease 0.4s both" }}>
                <Button variant="contained" size="large" onClick={() => openBookingModal("Blaze")} endIcon={<ArrowForwardIcon />}
                  sx={{ position: "relative", overflow: "hidden", bgcolor: GREEN, color: NAVY, fontWeight: 800, fontSize: 14.5, px: 3.5, py: 1.5, borderRadius: "12px", animation: "neonPulse 3s ease-in-out infinite", "&::before": { content: '""', position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, transparent 55%)" }, "&:hover": { bgcolor: "#86D422", transform: "translateY(-3px) scale(1.02)", boxShadow: `0 14px 38px ${GREEN}55` }, transition: "all 0.25s cubic-bezier(0.34,1.56,0.64,1)" }}>
                  Book Your Ride Now
                </Button>
                <Button variant="outlined" size="large" href="/#fleet" endIcon={<TrendingUpIcon />}
                  sx={{ color: "#A78BFA", borderColor: "#A78BFA50", fontWeight: 700, fontSize: 14, px: 3, py: 1.5, borderRadius: "12px", backdropFilter: "blur(8px)", bgcolor: "rgba(167,139,250,0.07)", "&:hover": { borderColor: "#A78BFA", bgcolor: "rgba(167,139,250,0.14)", color: "#C4B5FD", transform: "translateY(-2px)" }, transition: "all 0.22s ease" }}>
                  Rent @ ₹1,999 / Wk
                </Button>
                <Button variant="text" size="large" component="a" href="tel:+918123198675" startIcon={<PhoneIcon sx={{ fontSize: 16 }} />}
                  sx={{ color: "rgba(255,255,255,0.6)", fontWeight: 600, fontSize: 13.5, px: 2, py: 1.5, borderRadius: "12px", "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.07)" }, transition: "all 0.2s ease" }}>
                  +91 81231 98675
                </Button>
              </Stack>

              {/* Stats strip */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "14px", overflow: "hidden", backdropFilter: "blur(12px)", bgcolor: "rgba(255,255,255,0.03)", animation: "fadeUp 0.8s ease 0.5s both" }}>
                {STATS.map((s, i) => (
                  <Box key={s.label} sx={{ px: { xs: 1.2, sm: 2 }, py: 1.5, textAlign: "center", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.07)" : "none", "&:hover": { bgcolor: "rgba(255,255,255,0.04)" }, transition: "background 0.2s ease" }}>
                    <Typography sx={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: { xs: 14, sm: 16, md: 18 }, fontWeight: 800, color: s.color, lineHeight: 1, mb: 0.3 }}>{s.value}</Typography>
                    <Typography sx={{ fontSize: 10, color: "rgba(155,175,255,0.6)", fontWeight: 500 }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ══════════ RIGHT — Full Image Section ══════════ */}
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              animation: "fadeIn 1s ease 0.3s both",
              position: "relative",
            }}>
              {/* Radial ambient glow behind image */}
              <Box sx={{
                position: "absolute",
                top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: 320, md: 520 },
                height: { xs: 320, md: 520 },
                borderRadius: "50%",
                background: `radial-gradient(circle, ${GREEN}28 0%, ${BLUE}12 45%, transparent 70%)`,
                filter: "blur(50px)",
                pointerEvents: "none",
                zIndex: 0,
                animation: "pulseGlow 6s ease-in-out infinite",
              }} />

              {/* Full Image Container */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: "100%",
                  maxWidth: { xs: 440, md: 540, lg: 580 },
                  borderRadius: "22px",
                  border: `1px solid rgba(114, 191, 32, 0.25)`,
                  background: `rgba(4, 10, 28, 0.4)`,
                  backdropFilter: "blur(12px)",
                  overflow: "hidden",
                  boxShadow: `
                    0 24px 60px rgba(0, 0, 0, 0.75),
                    0 0 50px rgba(114, 191, 32, 0.18),
                    inset 0 0 0 1px rgba(255, 255, 255, 0.06)
                  `,
                  transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.015)",
                    borderColor: `${GREEN}60`,
                    boxShadow: `
                      0 28px 70px rgba(0, 0, 0, 0.8),
                      0 0 60px rgba(114, 191, 32, 0.28),
                      inset 0 0 0 1px rgba(255, 255, 255, 0.1)
                    `,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/assets/images/hero-hud.png"
                  alt="RideX City Live Fleet"
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    borderRadius: "20px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>

          </Box>
        </Container>
      </Box>
    </>
  );
}
